import React, { useEffect } from 'react';
import * as PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button, Table, Tag, Typography,
} from 'antd';
import { connect } from 'react-redux';
import { userIsAdminConsole } from '../../../../utility/security/security-util';
import ConfigHeading from '../../configure/components/ConfigHeading';
import { selectNotificationsPageDataRequest } from '../../../../state/ui/notification/NotificationUiReducer';
import { selectNotifications } from '../../../../state/domain/notification/NotificationReducer';
import { selectSessionUser } from '../../../../state/ui/application/sessionUiReducer';
import {
  editNotification,
  requestDeleteNotification,
  requestNotificationPageData,
} from '../../../../state/ui/notification/NotificationUiActions';
import EditNotification from './EditNotification';
import ConfirmButton from '../../../../components/buttons/ConfirmButton';
import {getDefaultLocale} from "../../../../configWeb";


const { Column } = Table;
const { Text } = Typography;

// TABLE
function NotificationsTable({
  isLoading, notifications, onEditButton, onDeleteButton, sessionUser,
}) {
  const intl = useIntl();

  const RowTotal = () => (
    <div>
      <Text>{`${notifications.length} `}</Text>
      <FormattedMessage id="items" />
    </div>
  );

  // FILTERS
  function filterType(value, record) {
    return record.notificationType === value;
  }

  // SORTS
  function sortMessage(a, b) {
    const aStr = `${a.message}`;
    const bStr = `${b.message}`;
    return aStr.localeCompare(bStr);
  }
  function sortByStartDate(a, b) {
    return new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime();
  }
  function sortByEndDate(a, b) {
    return new Date(a.endDateTime).getTime() - new Date(b.endDateTime).getTime();
  }

  // Date options
  const dateDisplayOptions = {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  };

  // Locale adjust
  const userLocale = sessionUser && sessionUser.locale ? sessionUser.locale.replace('_', '-') : getDefaultLocale().locale;

  // TABLE SETTINGS AND COLUMNS
  return (
    <Table
      pagination={{ defaultPageSize: 10, showTotal: () => <RowTotal /> }}
      scroll={{ x: 1, y: false }}
      loading={isLoading}
      dataSource={notifications}
      rowKey={notification => notification.notificationId}
    >
      <Column
        title={<FormattedMessage id="type" />}
        dataIndex="notificationType"
        key="notificationType"
        filters={[{ text: <FormattedMessage id="warning" />, value: 'warning' }, { text: <FormattedMessage id="info" />, value: 'info' }]}
        filteredValue={notifications.notificationType}
        onFilter={filterType}
        render={type => (
          <Tag color={type === 'info' ? 'blue' : 'gold'} key={Math.random()}>
            <FormattedMessage id={type === 'info' ? 'info' : 'warning'} />
          </Tag>
        )}
      />
      <Column
        sorter={sortMessage}
        title={<FormattedMessage id="message" />}
        dataIndex="message"
        key="message"
      />
      <Column
        sorter={sortByStartDate}
        defaultSortOrder="descend"
        title={<FormattedMessage id="from" />}
        dataIndex="startDateTime"
        key="startDateTime"
        render={dateVal => (dateVal ? new Date(dateVal).toLocaleDateString(userLocale, dateDisplayOptions) : null)}
      />
      <Column
        sorter={sortByEndDate}
        title={<FormattedMessage id="until" />}
        dataIndex="endDateTime"
        key="endDateTime"
        render={dateVal => (dateVal ? new Date(dateVal).toLocaleDateString(userLocale, dateDisplayOptions) : null)}
      />
      {userIsAdminConsole(sessionUser)
        ? (
          <Column
            className="via-action-column"
            align="center"
            width="200px"
            render={(text, record) => {
              const fullDelete = new Date(record.startDateTime) > new Date();
              const notificationRemove = { ...record, active: false };
              return (
                <div>
                  <ConfirmButton size="small" type="danger" onClick={() => onDeleteButton(notificationRemove, intl.formatMessage({ id: 'notification' }), fullDelete)} icon="stop">
                    <FormattedMessage id={fullDelete ? 'delete' : 'remove'} />
                  </ConfirmButton>
                  <Button size="small" type="primary" style={{ marginLeft: '0.5em' }} onClick={() => onEditButton(record)} icon="edit">
                    <FormattedMessage id="edit" />
                  </Button>
                </div>
              );
            }}
          />
        )
        : ''}
    </Table>
  );
}

NotificationsTable.propTypes = {
  isLoading: PropTypes.bool,
  onEditButton: PropTypes.func.isRequired,
  onDeleteButton: PropTypes.func.isRequired,
  notifications: PropTypes.arrayOf(PropTypes.object),
  sessionUser: PropTypes.shape({ locale: PropTypes.string.isRequired }).isRequired,
};

NotificationsTable.defaultProps = {
  isLoading: false,
  notifications: [],
};

function Notifications({
  isLoading, loadPageData, notifications, editNotificationPopup, deleteNotification, sessionUser,
}) {
  // Load inspection reasons and checklists on first entry - these only need to be loaded once.
  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  // TITLE ROW
  const CreateButton = () => (
    <Button type="primary" onClick={() => editNotificationPopup(null)} loading={isLoading} icon="plus">
      <FormattedMessage id="create" />
    </Button>
  );

  // MAIN COMPONENT
  return (
    <ConfigHeading
      titleId="notifications"
      descriptionId="notificationsDesc"
      actions={(
          userIsAdminConsole(sessionUser)
            ? <CreateButton />
            : <></>
      )}
    >
      <NotificationsTable
        isLoading={isLoading}
        notifications={notifications}
        onEditButton={editNotificationPopup}
        onDeleteButton={deleteNotification}
        sessionUser={sessionUser}
      />
      <EditNotification />
    </ConfigHeading>
  );
}

// Validate the prop type parameters.
Notifications.propTypes = {
  isLoading: PropTypes.bool,
  loadPageData: PropTypes.func.isRequired,
  editNotificationPopup: PropTypes.func.isRequired,
  deleteNotification: PropTypes.func.isRequired,
  notifications: PropTypes.arrayOf(PropTypes.object),
  sessionUser: PropTypes.shape({ displayName: PropTypes.string.isRequired }).isRequired,
};

Notifications.defaultProps = {
  isLoading: false,
  notifications: [],
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const pageDataRequest = selectNotificationsPageDataRequest(state);
  const notificationsAll = selectNotifications(state);
  const notifications = notificationsAll.filter(notify => notify.active);

  return {
    isLoading: pageDataRequest ? pageDataRequest.isLoading : false,
    error: pageDataRequest ? pageDataRequest.error : null,
    notifications,
    sessionUser: selectSessionUser(state),
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: () => dispatch(requestNotificationPageData()),
  editNotificationPopup: notification => dispatch(editNotification(notification ? notification.notificationId : null)),
  deleteNotification: (notification, label, fullDelete) => dispatch(requestDeleteNotification(notification, label, fullDelete)),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(Notifications);
